import React, { useMemo, useRef, useState } from 'react';
import { Storage } from '@capacitor/storage';
import {
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonPage,
  IonButtons,
  IonButton,
  IonList,
  IonItem,
  IonLabel,
  IonInput,
  IonText,
  IonModal,
  IonIcon,
  IonAlert,
  IonSearchbar,
  IonSpinner,
  useIonRouter
} from '@ionic/react';
import { toastController } from '@ionic/core';
import { logIn, openOutline } from 'ionicons/icons';
import './Login.scss';
import {
  AuthenticationApiError,
  configService
} from '../services/ConfigService';
import { getAppConfig, isFeatureEnabled } from '../config/app.config';
import { loginWithEvent as loginWithEventService } from '../data/eventsModal/events.service';
import {
  setIsLoggedIn,
  setUsername,
  setUserCed,
  setUserID,
  setUserOther,
  setEventCode,
  setEventName,
  setEventStartDate,
  setEventEndDate,
  setEventLocation
} from '../data/user/user.actions';
import { connect } from '../data/connect';
import { RouteComponentProps } from 'react-router';
import { loadConfData } from '../data/sessions/sessions.actions';
import FavoritesService from '../services/FavoritesService';
import { startDataService } from '../services/StartDataService';
import NetworkingService from '../services/NetworkingService';
import FirebaseTokenService from '../services/FirebaseTokenService';
import {
  emailPasswordAuthenticationService,
  FirebaseVerificationCooldownError
} from '../services/EmailPasswordAuthenticationService';
import {
  getAccessRequestErrorMessage,
  getAuthenticationFormVisibility,
  getCredentialAuthenticationErrorMessage,
  getPostLoginAuthenticationActions
} from '../services/AuthenticationFlow';
import { auth } from '../config/firebase.config';

interface OwnProps extends RouteComponentProps {}

interface DispatchProps {
  setIsLoggedIn: typeof setIsLoggedIn;
  setUsername: typeof setUsername;
  setUserCed: typeof setUserCed;
  setUserID: typeof setUserID;
  setUserOther: typeof setUserOther;
  setEventCode: typeof setEventCode;
  setEventName: typeof setEventName;
  setEventStartDate: typeof setEventStartDate;
  setEventEndDate: typeof setEventEndDate;
  setEventLocation: typeof setEventLocation;
  loadConfData: typeof loadConfData;
}

interface Evento {
  texto2: string;  // Nombre del evento
  texto3?: string; // Fecha y hora de inicio
  texto4?: string; // Fecha y hora de finalización
  texto6?: string; // Ubicación del evento
  eventCode: string;
}

interface EventoCert {
  texto2?: string;  // Nombre del evento
  texto3?: string; // Fecha y hora de inicio
  texto4?: string; // Fecha y hora de finalización
  texto6?: string; // Ubicación del evento
  eventCode: string;
}

interface LoginProps extends OwnProps, DispatchProps {}

const Login: React.FC<LoginProps> = ({
  setIsLoggedIn,
  history,
  setUsername: setUsernameAction,
  setUserCed: setUserCedAction,
  setUserID: setUserIDAction,
  setUserOther: setUserOtherAction,
  setEventCode: setEventCodeAction,
  setEventName: setEventNameAction,
  setEventStartDate: setEventStartDateAction,
  setEventEndDate: setEventEndDateAction,
  setEventLocation: setEventLocationAction,
  loadConfData: loadConfDataAction
}) => {
  const ionRouter = useIonRouter();
  const loginContentRef = useRef<HTMLIonContentElement>(null);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [usernameError, setUsernameError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);


  // Obtener configuración de la app
  const appConfig = getAppConfig();

  // Estados para el modal de eventos
  const [showModal, setShowModal] = useState(false);
  const [eventos, setEventos] = useState<Evento[]>([]);
  const [eventosCert, setEventosCert] = useState<EventoCert[]>([]);
  const [eventoError, setEventoError] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertHeader, setAlertHeader] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingEvent, setLoadingEvent] = useState<string | null>(null);
  const [loadingCertEvent, setLoadingCertEvent] = useState<string | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [authenticatedCedula, setAuthenticatedCedula] = useState('');
  const [showActivation, setShowActivation] = useState(false);
  const [activationEmail, setActivationEmail] = useState('');
  const [isRequestingAccess, setIsRequestingAccess] = useState(false);
  const formVisibility = getAuthenticationFormVisibility(configService.getAuthenticationMode());
  const usesEmailPassword = formVisibility.showEmailPassword;

  const revealFocusedLoginControl = (event: CustomEvent<FocusEvent>) => {
    const input = event.currentTarget as HTMLElement | null;
    const control = input?.closest('.login-input') as HTMLElement | null;

    window.setTimeout(async () => {
      const content = loginContentRef.current;
      if (!content || !control) return;

      const scrollElement = await content.getScrollElement();
      const scrollRect = scrollElement.getBoundingClientRect();
      const controlRect = control.getBoundingClientRect();
      const desiredTop = scrollElement.scrollTop
        + controlRect.top
        - scrollRect.top
        - Math.max(16, (scrollRect.height - controlRect.height) / 2);

      await content.scrollToPoint(0, Math.max(0, desiredTop), 250);
    }, 350);
  };

  const showMessage = (header: string, message: string) => {
    setAlertHeader(header);
    setAlertMessage(message);
    setShowAlert(true);
  };

  const openAccessRequest = () => {
    setActivationEmail(username.trim().toLowerCase());
    setShowActivation(true);
  };

  const requestAccountAccess = async () => {
    const email = activationEmail.trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      showMessage('Activar o recuperar acceso', 'Ingrese un correo electrónico válido.');
      return;
    }

    setIsRequestingAccess(true);
    try {
      const result = await configService.requestFirebaseAccess({ email });
      setUsername(email);
      setPassword('');
      setShowActivation(false);
      showMessage('Activar o recuperar acceso', result.message);
    } catch (error) {
      const code = error instanceof AuthenticationApiError ? error.code : 'UNKNOWN';
      showMessage(
        'No se pudo solicitar el acceso',
        getAccessRequestErrorMessage(code)
      );
    } finally {
      setIsRequestingAccess(false);
    }
  };

  const resendEmailVerification = async () => {
    try {
      await emailPasswordAuthenticationService.sendEmailVerification();
      const toast = await toastController.create({
        message: 'Correo de verificación enviado.',
        duration: 3500,
        color: 'success'
      });
      await toast.present();
    } catch (error) {
      const message = error instanceof FirebaseVerificationCooldownError
        ? `Espere ${error.remainingSeconds} segundos antes de reenviar la verificación.`
        : 'No se pudo reenviar la verificación en este momento.';
      const toast = await toastController.create({ message, duration: 4500, color: 'warning' });
      await toast.present();
    }
  };

  const processFirebaseLoginMetadata = async (data: any) => {
    if (!usesEmailPassword) {
      if (data.firebaseCustomToken) {
        try {
          await auth.signInWithCustomToken(data.firebaseCustomToken);
          data.firebaseSessionEstablished = true;
          delete data.firebaseCustomToken;
        } catch (error) {
          console.warn('Firebase: no se pudo iniciar la sesión autorizada del modo por cédula.');
          delete data.firebaseCustomToken;
        }
      }
      return;
    }
    const actions = getPostLoginAuthenticationActions(data);

    if (actions.refreshFirebaseUser) {
      try {
        await emailPasswordAuthenticationService.refreshCurrentUser();
        if (data.email) setUsername(String(data.email));
      } catch (error) {
        console.warn('Firebase: no se pudo refrescar la sesión después del cambio de correo.');
      }
    }

    if (actions.recordProfileSyncPending) {
      await Storage.set({
        key: 'auth_profile_sync_pending',
        value: JSON.stringify({
          eventcode: data.eventcode || configService.getDefaultEventID() || '',
          uid: data.uid || data.id || '',
          recordedAt: new Date().toISOString()
        })
      });
      console.warn('Firebase: sincronización de perfil pendiente.', {
        eventcode: data.eventcode || configService.getDefaultEventID() || ''
      });
    }

    if (actions.showVerificationNotice) {
      const toast = await toastController.create({
        header: 'Verifique su correo',
        message: 'Puede continuar usando la app. Reenvíe la verificación si no recibió el mensaje.',
        duration: 10000,
        buttons: [
          {
            text: 'Reenviar verificación',
            handler: () => {
              void resendEmailVerification();
            }
          },
          { text: 'Cerrar', role: 'cancel' }
        ]
      });
      await toast.present();
    }
  };

  // Función para hacer login directo con un evento específico
  const selectEventDirectly = async (evento: any, userced: string) => {
    console.log("Ejecutando login directo con evento:", evento);

    await loginWithEventService(
      evento,
      userced,
      setIsLoggedIn,
      setUsernameAction,
      setUserCedAction,
      setUserIDAction,
      setUserOtherAction,
      setEventCodeAction,
      setEventNameAction,
      setEventStartDateAction,
      setEventEndDateAction,
      setEventLocationAction,
      () => {}, // setShowEventsModal - no necesario aquí
      history,
      undefined, // setLoading - opcional
      undefined, // setEventoError - opcional
      loadConfDataAction // ✅ NUEVO: Pasar la acción para cargar datos de conferencia
    );
  };

  // Función de login inicial
  const login = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitted(true);
    const hasUsername = !usesEmailPassword || Boolean(username.trim());
    const hasPassword = Boolean(password);
    setUsernameError(!hasUsername);
    setPasswordError(!hasPassword);

    if (hasUsername && hasPassword) {
      setIsLoggingIn(true);
      try {
        let loginCedula = password.trim();
        const response = usesEmailPassword
          ? await emailPasswordAuthenticationService.authenticate(username, password)
              .then(session => configService.loginWithFirebase(session.idToken))
          : await configService.loginMultiEvent(loginCedula);
        const data = await response.json();
        if (!response.ok || data.error) {
          const error = new Error(data.code || `HTTP_${response.status}`);
          (error as any).code = data.code;
          throw error;
        }

        await processFirebaseLoginMetadata(data);

        loginCedula = data.ced || loginCedula;
        setAuthenticatedCedula(loginCedula);
        console.log("Eventos recibidos:", data.eventos);

        {
          // Verificar si multievent está habilitado
          const isMultievent = configService.isMultieventEnabled();

          if (!isMultievent) {
            // Login directo - no mostrar modal, cargar evento directamente
            console.log("Login directo - cargando evento automáticamente");

            // Verificar que el login fue exitoso y hay datos del usuario
            if (data.username) {
              const eventCode = data.eventcode || configService.getDefaultEventID() || '';
              const eventName = data.texto2 || data.eventname || 'Evento';
              const userOther = `${data.company || ''}&&&${data.role || ''}`;

              // Guardar datos del usuario en localStorage
              await Storage.set({ key: 'userced', value: data.ced || loginCedula });
              await Storage.set({ key: 'username', value: data.username });
              await Storage.set({ key: 'eventname', value: eventName });
              await Storage.set({ key: 'eventcode', value: eventCode });

              // Actualizar y esperar el estado global antes de cargar el evento.
              await setUsernameAction(data.username);
              await setUserCedAction(data.ced || loginCedula);
              await setUserIDAction(data.id);
              await setUserOtherAction(userOther);
              await setEventCodeAction(eventCode);
              await setEventNameAction(eventName);
              await setEventStartDateAction(data.texto3 || '');
              await setEventEndDateAction(data.texto4 || '');
              await setEventLocationAction(data.texto6 || '');

              // Cargar la configuración y la agenda en Redux antes de navegar.
              await startDataService.loadLatestStartData(undefined, eventCode);
              await loadConfDataAction();

              // El flujo directo de un solo evento debe sincronizar favoritos
              // antes de navegar, igual que el flujo de selección de evento.
              // Sin esta inicialización, los favoritos aparecían solamente
              // después de recargar la aplicación.
              console.log('Login directo: Inicializando sincronización de favoritos...');
              try {
                const favoritesService = FavoritesService.getInstance();
                await favoritesService.initialize(data.id);
                console.log('Login directo: FavoritesService inicializado correctamente');
              } catch (error) {
                console.warn('Login directo: No se pudieron sincronizar los favoritos:', error);
              }

              await setIsLoggedIn(true);

              console.log("Usuario autenticado:", data.username);
              console.log("Evento:", eventName);
              console.log("Estado de login y datos del evento actualizados");

              // Redirigir directamente a la aplicación
              const defaultRoute = getAppConfig().auth.defaultRoute || '/tabs/schedule';
              ionRouter.push(defaultRoute, 'root', 'replace');
            } else {
              setAlertHeader("Error de autenticación");
              setAlertMessage("No se pudo completar el login. Intente nuevamente.");
              setShowAlert(true);
            }
          } else {
            // Multievent habilitado - verificar si hay evento por defecto para login directo
            console.log("Multievent habilitado - verificando evento por defecto");

            const defaultEventID = configService.getDefaultEventID();
            let shouldShowModal = true;

            // Verificamos la propiedad "eventos"
            if (data.eventos && Array.isArray(data.eventos) && data.eventos.length > 0) {
              setEventos(data.eventos);
              setEventoError("");

              // Si hay evento por defecto, buscar y hacer login directo
              if (defaultEventID) {
                console.log("Buscando evento por defecto para login directo:", defaultEventID);

                const defaultEvent = data.eventos.find((evento: any) =>
                  evento.eventCode === defaultEventID || evento.texto1 === defaultEventID
                );

                if (defaultEvent) {
                  console.log("Evento por defecto encontrado, ejecutando login directo sin modal:", defaultEvent);
                  shouldShowModal = false;

                  // Ejecutar login directo usando el servicio de eventos
                  setTimeout(async () => {
                    try {
                      await selectEventDirectly(defaultEvent, loginCedula);
                    } catch (error) {
                      console.error("Error en login directo:", error);
                      // Si falla, mostrar modal como fallback
                      setShowModal(true);
                    }
                  }, 100);
                }
              }
            } else {
              setEventos([]);
              setEventoError("No estás registrado en ningún evento");
            }

            // Verificamos si hay eventos certificados adicionales
            if (data.eventoscert && Array.isArray(data.eventoscert) && data.eventoscert.length > 0) {
              setEventosCert(data.eventoscert);

              // También verificar en eventos certificados si no se encontró en eventos normales
              if (defaultEventID && shouldShowModal) {
                console.log("Buscando evento por defecto en eventos certificados:", defaultEventID);

                const defaultCertEvent = data.eventoscert.find((evento: any) =>
                  evento.eventCode === defaultEventID || evento.texto1 === defaultEventID
                );

                if (defaultCertEvent) {
                  console.log("Evento por defecto encontrado en certificados, ejecutando login directo sin modal:", defaultCertEvent);
                  shouldShowModal = false;

                  setTimeout(async () => {
                    try {
                      await selectEventDirectly(defaultCertEvent, loginCedula);
                    } catch (error) {
                      console.error("Error en login directo con evento certificado:", error);
                      setShowModal(true);
                    }
                  }, 100);
                }
              }
            } else {
              setEventosCert([]);
            }

            // Solo mostrar modal si no se encontró evento por defecto
            if (shouldShowModal) {
              console.log("Mostrando modal de selección de eventos");
              setShowModal(true);
              setIsLoggingIn(false);
            } else {
              // Si no se muestra modal, significa que se redirigió automáticamente
              setIsLoggingIn(false);
            }
          }
        }
      } catch (error) {
        console.warn('Inicio de sesión rechazado.', {
          code: (error as any)?.code || (error instanceof Error ? error.message : 'UNKNOWN')
        });

        if (usesEmailPassword && error instanceof Error && !error.message.includes('503')) {
          setAlertHeader("Error de autenticación");
          setAlertMessage(getCredentialAuthenticationErrorMessage((error as any).code || error.message));
          setShowAlert(true);
        } else if (error instanceof Error && error.message.includes('503')) {
          setAlertHeader("Error de servidor");
          setAlertMessage("El servidor de desarrollo no está disponible en este momento. Por favor, contacte al administrador del sistema.");
          setShowAlert(true);
        } else {
          setAlertHeader("Error de conexión");
          setAlertMessage("No se pudo conectar con el servidor. Por favor, verifique su conexión a internet e intente nuevamente.");
          setShowAlert(true);
        }
        setIsLoggingIn(false);
      }
    } else {
      setIsLoggingIn(false);
    }
  };

  // Filtramos los eventos según el término de búsqueda
  const filteredEventos = useMemo(() => {
    if (!searchTerm.trim()) return eventos;

    const searchTermLower = searchTerm.toLowerCase().trim();
    return eventos.filter(evento => {
      // Buscamos en el nombre del evento
      const nameMatch = evento.texto2?.toLowerCase().includes(searchTermLower);
      // Buscamos en la fecha de inicio
      const startDateMatch = evento.texto3?.toLowerCase().includes(searchTermLower);
      // Buscamos en la fecha de finalización
      const endDateMatch = evento.texto4?.toLowerCase().includes(searchTermLower);
      // Buscamos en la ubicación
      const locationMatch = evento.texto6?.toLowerCase().includes(searchTermLower);

      return nameMatch || startDateMatch || endDateMatch || locationMatch;
    });
  }, [eventos, searchTerm]);

  // Filtramos los eventos certificados según el término de búsqueda
  const filteredEventosCert = useMemo(() => {
    if (!searchTerm.trim()) return eventosCert;

    const searchTermLower = searchTerm.toLowerCase().trim();
    return eventosCert.filter(eventoCert => {
      // Buscamos en el nombre del evento
      const nameMatch = (eventoCert.texto2 || `Evento ${eventoCert.eventCode}`)?.toLowerCase().includes(searchTermLower);
      // Buscamos en la fecha de inicio
      const startDateMatch = eventoCert.texto3?.toLowerCase().includes(searchTermLower);
      // Buscamos en la fecha de finalización
      const endDateMatch = eventoCert.texto4?.toLowerCase().includes(searchTermLower);
      // Buscamos en la ubicación
      const locationMatch = eventoCert.texto6?.toLowerCase().includes(searchTermLower);

      return nameMatch || startDateMatch || endDateMatch || locationMatch;
    });
  }, [eventosCert, searchTerm]);

  // Función para hacer login nuevamente con el evento seleccionado
  const loginWithEvent = async (evento: Evento) => {
    // Establecemos el evento que está cargando
    setLoadingEvent(evento.texto2);

    try {
      // Usar ConfigService para login con evento específico
      const response = usesEmailPassword
        ? await emailPasswordAuthenticationService.getCurrentIdToken()
            .then(idToken => configService.loginWithFirebase(idToken, evento.eventCode))
        : await configService.loginSingleEvent(authenticatedCedula || password, evento.eventCode);
      const data = await response.json();
      if (!response.ok || data.error) {
        const error = new Error(data.code || `HTTP_${response.status}`);
        (error as any).code = data.code;
        throw error;
      }
      await processFirebaseLoginMetadata(data);
      console.log("Login con evento:", data);

      {
        // Actualizamos el store y redirigimos a la pantalla principal
        await setIsLoggedIn(true);
        setUsernameAction(data.username);
        setUserCedAction(data.ced);
        setUserIDAction(data.id);
        setUserOtherAction(data.company.concat("&&&", data.role));

        // ✅ CORREGIDO: Esperar a que se guarde el eventcode en Storage antes de cargar datos
        await setEventCodeAction(data.eventcode);

        // Cargar la configuración específica del evento, incluido el tutorial.
        await startDataService.loadLatestStartData(undefined, data.eventcode);

        console.log(data);
        // Guardamos la información del evento
        setEventNameAction(data.texto2 || evento.texto2 || '');
        setEventStartDateAction(data.texto3 || '');
        setEventEndDateAction(data.texto4 || '');
        setEventLocationAction(data.texto6 || '');

        // ✅ CORREGIDO: Recargar datos de la conferencia DESPUÉS de guardar eventcode
        console.log('🔄 Login: Ejecutando loadConfDataAction...');
        await loadConfDataAction();
        console.log('✅ Login: loadConfDataAction ejecutado');

        console.log('Login: Inicializando sincronización de favoritos...');
        try {
          const favoritesService = FavoritesService.getInstance();
          await favoritesService.initialize(data.id);

          // La sincronización forzada se maneja automáticamente por el servicio

          console.log('Login: FavoritesService inicializado y sincronizado correctamente');
        } catch (error) {
          console.warn('Login: Error al inicializar FavoritesService:', error);
        }

        // Inicializar perfil de networking si está habilitado
        console.log('Login: Verificando si networking está habilitado:', isFeatureEnabled('networking'));
        if (isFeatureEnabled('networking')) {
          try {
            console.log('Login: Inicializando perfil de networking...');
            console.log('Login: Datos completos del participante:', JSON.stringify(data, null, 2));

            // El modo cédula conserva su autenticación anónima legacy. En el
            // modo correo la sesión Firebase ya existe y no debe reemplazarse.
            if (!usesEmailPassword && !data.firebaseSessionEstablished) {
              console.log('Login: Inicializando autenticación Firebase legacy...');
              const firebaseTokenService = FirebaseTokenService.getInstance();
              await firebaseTokenService.getFirebaseToken(data);
              console.log('Login: Autenticación Firebase legacy completada');
            }

            const networkingService = NetworkingService.getInstance();

            // Mapear campos correctos de la tabla de participantes
            const fullName = `${data.texto1 || ''} ${data.texto2 || ''}`.trim();

            // Log detallado de los campos que estamos usando
            console.log('Login: Campos de networking extraídos:', {
              texto1: data.texto1,
              texto2: data.texto2,
              texto3: data.texto3,
              texto8: data.texto8,
              texto9: data.texto9,
              fullName: fullName,
              username: data.username,
              company: data.company
            });

            await networkingService.initializeProfile({
              id: data.id,
              name: fullName || data.username || '', // texto1 + texto2 (nombres y apellidos) o username como fallback
              email: data.texto3 || data.email || '', // texto3 es el email
              phone: data.texto9 || data.phone || '', // texto9 es el teléfono
              company: data.texto8 || data.company || '', // texto8 es la empresa
              position: data.position || '', // Si hay campo de cargo en el futuro
              eventCode: data.eventcode
            });

            console.log('Login: Perfil de networking inicializado exitosamente');
          } catch (error) {
            console.error('Login: Error al inicializar perfil de networking:', error);
            if (error instanceof Error) {
              console.error('Login: Stack trace:', error.stack);
            }
            // No interrumpir el flujo de login por errores de networking
          }
        } else {
          console.log('Login: Networking no está habilitado, saltando inicialización');
        }

        setShowModal(false);
        const defaultRoute = getAppConfig().auth.defaultRoute || '/tabs/schedule';
        ionRouter.push(defaultRoute, 'root', 'replace');
      }
    } catch (error) {
      console.error("Error durante el inicio de sesión con evento:", error);

      // Verificar si es un error 503
      if (error instanceof Error && error.message.includes('503')) {
        setAlertHeader("Error de servidor");
        setAlertMessage("El servidor de desarrollo no está disponible en este momento. Por favor, contacte al administrador del sistema.");
        setShowAlert(true);
      } else {
        setAlertHeader("Error de conexión");
        setAlertMessage("No se pudo conectar con el servidor. Por favor, verifique su conexión a internet e intente nuevamente.");
        setShowAlert(true);
      }
    } finally {
      // Limpiamos el estado de carga del evento
      setLoadingEvent(null);
    }
  };

  // Función para abrir eventos certificados
  const openCertEvent = (eventoCert: EventoCert) => {
    // Establecemos el evento que está cargando
    setLoadingCertEvent(eventoCert.texto2 || `Evento ${eventoCert.eventCode}`);

    // Usamos setTimeout para dar tiempo a que se muestre el indicador de carga
    setTimeout(() => {
      window.open(`https://www.esimposio.com/e/${eventoCert.eventCode}/${authenticatedCedula || password}`, '_blank');
      // Limpiamos el estado de carga
      setLoadingCertEvent(null);
    }, 500);
  };

  return (
    <IonPage id="login-page">
      <IonContent ref={loginContentRef} className="login-content" scrollY={true}>
        <div className="login-container">
          {/* Left side with logo and welcome text */}
          <div className="login-left-panel" style={{ background: 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(5px)' }}>
            <div className="login-logo ion-text-center">
              <img src="assets/icon/favicon.png" alt="IESTEC LOGO" />
              <h2 style={{ color: 'var(--neutral-900)', fontWeight: '600' }}>Bienvenido</h2>
              <p style={{ color: 'var(--neutral-700)' }}>
                {usesEmailPassword
                  ? 'Por favor ingrese su correo electrónico y clave.'
                  : 'Por favor ingrese su identificación.'}
              </p>
            </div>
          </div>

          {/* Right side with login form */}
          <div className="login-right-panel" style={{ background: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)', borderRadius: '12px', boxShadow: 'var(--shadow-md)' }}>
            <h2 className="login-title" style={{ color: 'var(--neutral-900)', fontWeight: '600' }}>Iniciar Sesión</h2>

            <form noValidate onSubmit={login} className="login-form">
                {usesEmailPassword && (
                  <>
                    <IonItem lines="none" className="login-input" style={{ '--background': 'rgba(255, 255, 255, 0.5)', '--color': 'var(--neutral-900)', borderRadius: '8px', border: '1px solid var(--neutral-300)' }}>
                      <IonLabel position="floating" style={{ color: 'var(--neutral-700)' }}>Correo electrónico</IonLabel>
                      <IonInput
                        name="email"
                        type="email"
                        autocomplete="email"
                        value={username}
                        onIonFocus={revealFocusedLoginControl}
                        onIonChange={e => setUsername(e.detail.value || '')}
                      />
                    </IonItem>

                    {formSubmitted && usernameError && (
                      <IonText color="danger">
                        <p className="ion-padding-start">El correo electrónico es requerido.</p>
                      </IonText>
                    )}
                  </>
                )}

                <IonItem lines="none" className="login-input" style={{ '--background': 'rgba(255, 255, 255, 0.5)', '--color': 'var(--neutral-900)', borderRadius: '8px', border: '1px solid var(--neutral-300)' }}>
                  <IonLabel position="floating" style={{ color: 'var(--neutral-700)' }}>
                    {usesEmailPassword ? 'Clave' : 'Identificación'}
                  </IonLabel>
                  <IonInput
                    name="password"
                    type={usesEmailPassword || !appConfig.auth.showPasswordAsText ? "password" : "text"}
                    autocomplete={usesEmailPassword ? "current-password" : "off"}
                    value={password}
                    onIonFocus={revealFocusedLoginControl}
                    onIonChange={e => setPassword(e.detail.value || '')}
                  />
                </IonItem>

                {formSubmitted && passwordError && (
                  <IonText color="danger">
                    <p className="ion-padding-start">
                      {usesEmailPassword ? 'La clave es requerida.' : 'La identificación es requerida.'}
                    </p>
                  </IonText>
                )}

                <IonButton
                  type="submit"
                  expand="block"
                  className="ion-margin-top"
                  disabled={isLoggingIn}
                  style={{ '--background': 'var(--primary-dark)', '--color': 'white', '--border-radius': '8px', fontWeight: '600', height: '44px' }}
                >
                  {isLoggingIn ? (
                    <>
                      <IonSpinner name="crescent" slot="start" style={{ marginRight: '8px' }} />
                      Iniciando...
                    </>
                  ) : (
                    <>
                      <IonIcon slot="start" icon={logIn}></IonIcon>
                      Ingresar
                    </>
                  )}
                </IonButton>

                {usesEmailPassword && appConfig.auth.allowAccountActivation !== false && (
                  <div className="ion-text-center ion-margin-top">
                    <IonButton fill="clear" size="small" type="button" onClick={openAccessRequest}>
                      Olvidé mi contraseña
                    </IonButton>
                    <IonButton fill="clear" size="small" type="button" onClick={openAccessRequest}>
                      Activar cuenta o recuperar acceso
                    </IonButton>
                  </div>
                )}
            </form>
          </div>
        </div>

        <IonModal isOpen={showActivation} onDidDismiss={() => setShowActivation(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Activar o recuperar acceso</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowActivation(false)}>Cerrar</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <p>Ingrese el correo registrado para el evento. Si existe una cuenta asociada, recibirá las instrucciones.</p>
            <IonList>
              <IonItem>
                <IonLabel position="floating">Correo electrónico</IonLabel>
                <IonInput
                  type="email"
                  value={activationEmail}
                  onIonChange={e => setActivationEmail(e.detail.value || '')}
                  autocomplete="email"
                />
              </IonItem>
            </IonList>
            <IonButton expand="block" className="ion-margin-top" disabled={isRequestingAccess} onClick={requestAccountAccess}>
              {isRequestingAccess && <IonSpinner name="crescent" slot="start" />}
              {isRequestingAccess ? 'Enviando...' : 'Enviar instrucciones'}
            </IonButton>
          </IonContent>
        </IonModal>

        {/* Modal para mostrar el listado de eventos o mensaje de error */}
        <IonModal isOpen={showModal} onDidDismiss={() => setShowModal(false)} className="events-modal">
          <IonHeader>
            <IonToolbar className="modal-toolbar">
              <IonTitle>Mis Eventos</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowModal(false)}>Cerrar</IonButton>
              </IonButtons>
            </IonToolbar>
            {!eventoError && (eventos.length > 0 || eventosCert.length > 0) && (
              <IonToolbar>
                <IonSearchbar
                  value={searchTerm}
                  onIonChange={(e) => setSearchTerm(e.detail.value || '')}
                  placeholder="Buscar por nombre, fecha o ubicación..."
                  animated={true}
                  debounce={300}
                  showCancelButton="never"
                  style={{
                    '--background': document.body.classList.contains('dark-theme') ? '#1e1e1e' : 'rgba(255, 255, 255, 0.8)',
                    '--color': document.body.classList.contains('dark-theme') ? 'white' : 'black',
                    '--placeholder-color': document.body.classList.contains('dark-theme') ? 'rgba(255, 255, 255, 0.6)' : undefined,
                    'border': document.body.classList.contains('dark-theme') ? '1px solid rgba(255, 255, 255, 0.1)' : undefined,
                    'borderRadius': '8px'
                  }}
                />
              </IonToolbar>
            )}
          </IonHeader>
          <IonContent className="modal-content">
            {eventoError ? (
              <div className="error-message ion-padding ion-text-center ion-margin" style={{ background: 'white', borderRadius: '12px', border: '1px solid #ffcdd2', boxShadow: 'var(--shadow-sm)' }}>
                <IonText color="danger">
                  <p>{eventoError}</p>
                </IonText>
              </div>
            ) : searchTerm.trim() !== '' && filteredEventos.length === 0 && filteredEventosCert.length === 0 ? (
              <div className="ion-padding ion-text-center ion-margin" style={{ background: 'white', borderRadius: '12px', border: '1px solid var(--neutral-200)', boxShadow: 'var(--shadow-sm)' }}>
                <IonText>
                  <p>No se encontraron eventos que coincidan con la búsqueda "{searchTerm}".</p>
                </IonText>
              </div>
            ) : (
              <>
                {filteredEventos.length > 0 && (
                  <div className="events-container ion-padding ion-margin" style={{ background: 'white', borderRadius: '12px', border: '1px solid var(--neutral-200)', boxShadow: 'var(--shadow-sm)' }}>
                    <h2 className="ion-text-center" style={{ color: 'var(--neutral-900)', fontWeight: '600' }}>Actuales actividades académicas:</h2>
                    <IonList className="events-list">
                      {filteredEventos.map((evento, index) => (
                        <IonItem
                          key={index}
                          button
                          onClick={() => loadingEvent === null && loginWithEvent(evento)}
                          lines="none"
                          className="event-item"
                          style={{
                            '--background': 'rgba(255, 255, 255, 0.5)',
                            '--color': 'var(--neutral-900)',
                            borderRadius: '8px',
                            border: '1px solid var(--neutral-300)',
                            margin: '8px 0',
                            position: 'relative'
                          }}
                        >
                          {loadingEvent === evento.texto2 ? (
                            <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '15px' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                                <IonSpinner name="crescent" style={{ color: 'var(--ion-color-primary)' }} />
                                <p style={{ margin: 0, color: 'var(--ion-color-primary)', fontWeight: 'bold' }}>Entrando...</p>
                              </div>
                            </div>
                          ) : (
                            <IonLabel>
                              <h2 style={{ fontWeight: 'bold', fontSize: '16px', whiteSpace: 'normal', overflow: 'visible' }}>
                                {evento.texto2}
                              </h2>
                              {(evento.texto3 || evento.texto4) && (
                                <p style={{ color: 'var(--neutral-700)', fontSize: '14px', marginTop: '4px' }}>
                                  <span style={{ display: 'block' }}>
                                    <strong>Inicio:</strong> {evento.texto3 || 'No disponible'}
                                  </span>
                                  <span style={{ display: 'block' }}>
                                    <strong>Fin:</strong> {evento.texto4 || 'No disponible'}
                                  </span>
                                  {evento.texto6 && (
                                    <span style={{ display: 'block' }}>
                                      <strong>Ubicación:</strong> {evento.texto6}
                                    </span>
                                  )}
                                </p>
                              )}
                            </IonLabel>
                          )}
                        </IonItem>
                      ))}
                    </IonList>
                  </div>
                )}
              </>
            )}

            {/* Sección de otros eventos (eventoscert) */}
            {eventosCert.length > 0 && filteredEventosCert.length > 0 && (
              <div className="other-events-container ion-padding ion-margin" style={{ background: 'white', borderRadius: '12px', border: '1px solid var(--neutral-200)', boxShadow: 'var(--shadow-sm)', marginTop: '20px' }}>
                <h2 className="ion-text-center" style={{ color: 'var(--neutral-900)', fontWeight: '600' }}>Anteriores eventos:</h2>
                <IonList className="events-list">
                  {filteredEventosCert.map((eventoCert, index) => (
                    <IonItem
                      key={`cert-${index}`}
                      button
                      onClick={() => loadingCertEvent === null && openCertEvent(eventoCert)}
                      lines="none"
                      className="event-item"
                      style={{
                        '--background': 'rgba(255, 255, 255, 0.5)',
                        '--color': 'var(--ion-color-primary)',
                        borderRadius: '8px',
                        border: '1px solid var(--neutral-300)',
                        margin: '8px 0',
                        position: 'relative'
                      }}
                    >
                      {loadingCertEvent === (eventoCert.texto2 || `Evento ${eventoCert.eventCode}`) ? (
                        <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '15px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                            <IonSpinner name="crescent" style={{ color: 'var(--ion-color-primary)' }} />
                            <p style={{ margin: 0, color: 'var(--ion-color-primary)', fontWeight: 'bold' }}>Entrando...</p>
                          </div>
                        </div>
                      ) : (
                        <IonLabel>
                          <h2 style={{ fontWeight: 'bold', fontSize: '16px', whiteSpace: 'normal', overflow: 'visible' }}>
                            {eventoCert.texto2 || `Evento ${eventoCert.eventCode}`}
                          </h2>
                          {(eventoCert.texto3 || eventoCert.texto4) && (
                            <p style={{ color: 'var(--neutral-700)', fontSize: '14px', marginTop: '4px' }}>
                              <span style={{ display: 'block' }}>
                                <strong>Inicio:</strong> {eventoCert.texto3 || 'No disponible'}
                              </span>
                              <span style={{ display: 'block' }}>
                                <strong>Fin:</strong> {eventoCert.texto4 || 'No disponible'}
                              </span>
                              {eventoCert.texto6 && (
                                <span style={{ display: 'block' }}>
                                  <strong>Ubicación:</strong> {eventoCert.texto6}
                                </span>
                              )}
                            </p>
                          )}
                        </IonLabel>
                      )}
                      {!loadingCertEvent && <IonIcon icon={openOutline} slot="end" />}
                    </IonItem>
                  ))}
                </IonList>
              </div>
            )}
          </IonContent>
        </IonModal>

        {/* Alerta para mostrar mensajes de error */}
        <IonAlert
          isOpen={showAlert}
          onDidDismiss={() => setShowAlert(false)}
          header={alertHeader}
          message={alertMessage}
          buttons={['OK']}
          cssClass="error-alert"
        />
      </IonContent>
    </IonPage>
  );
};

export default connect<OwnProps, {}, DispatchProps>({
  mapDispatchToProps: {
    setIsLoggedIn,
    setUsername,
    setUserCed,
    setUserID,
    setUserOther,
    setEventCode,
    setEventName,
    setEventStartDate,
    setEventEndDate,
    setEventLocation,
    loadConfData
  },
  component: Login
});
